import {
  FilterPresenceDetails,
  RowsBucketType,
  WorklistMode,
  WorklistModeType,
  WorklistTypeDetail,
  WorklistTypeDetailType,
} from './state';
import { ProcessedFilterValueType } from '../WorklistFilters/types';
import { CustomWorklist, SortColumn } from 'generated/graphql';
import { SORTABLE_WORKLIST_COLUMN_MAP_REVERSED } from 'config/constants';
import { SortingState } from '@tanstack/react-table';

/**
 *
 * @param filterValue - the processed filter value to check
 * @returns true if the filter is present (i.e. has a value that should be sent to the backend), false otherwise
 */
export const isFilterPresent = (filterValue: ProcessedFilterValueType): boolean => {
  if (filterValue == null) {
    return false;
  }

  if (Array.isArray(filterValue)) {
    return !filterValue.every((v) => v == null) && filterValue.length > 0;
  }

  if (typeof filterValue === 'boolean') {
    return true;
  }

  if (typeof filterValue === 'string') {
    return filterValue.trim().length > 0;
  }

  return false;
};

/**
 *
 * @param n - the number to bucket
 * @param buckets - the upper bounds of each bucket
 * @param labels - the labels for each bucket; must be one more than the number of buckets
 * @returns - the label for the bucket that n falls into
 *
 * For example, with buckets [0, 2, 5] and labels ['0', '1-2', '3-5', '6+']:
 * - n = 0 => '0'
 * - n = 1 => '1-2'
 * - n = 2 => '1-2'
 */
export const getBucketCount = (n: number, buckets: number[], labels: string[]) => {
  const i = buckets.findIndex((e) => n <= e);
  return labels[i === -1 ? labels.length - 1 : i];
};

/**
 *
 * @param n - the number to bucket
 * @returns - the label for the bucket that n falls into
 *
 */
export const getRowBucketCount = (n: number) => {
  return getBucketCount(
    n,
    [0, 50, 200, 500, 1000, 5000],
    ['0', '1-50', '51-200', '201-500', '500-1000', '1000-5000', '5000+']
  ) as RowsBucketType;
};

/**
 *
 * @param n - the number to bucket
 * @returns - the label for the bucket that n falls into
 *
 */
export const getPriorsBucketCount = (n: number) => {
  return getBucketCount(
    n,
    [0, 2, 5, 10, 15, 20, 50, 100],
    ['0', '1-2', '3-5', '6-10', '11-15', '16-20', '21-50', '51-100', '100+']
  ) as RowsBucketType;
};

/**
 *
 * @param sortColumns - array of sort column IDs
 * @param sortOrders - array of sort orders ('asc' or 'desc')
 * @returns an object with a 'sorting' key containing an array of "column:direction" strings
 */
export function buildSortDetails(
  sortColumns: Nullable<Array<SortColumn>>,
  sortOrders: Nullable<Array<string>>
) {
  const details: Record<string, unknown> = {};
  if (sortColumns == null || sortOrders == null || sortColumns.length !== sortOrders.length) {
    return details;
  }

  return {
    sorting: sortColumns.map(
      (col, idx) =>
        `${SORTABLE_WORKLIST_COLUMN_MAP_REVERSED[col]}:${sortOrders[idx]?.toLowerCase()}`
    ),
  };
}

/**
 *
 * @param previous - previous sorting state
 * @param current  - current sorting state
 * @returns - array of changes in the format "column:direction" or "column:removed"
 */
export function computeChangedSorting(
  previous: Nullable<SortingState>,
  current: SortingState
): string[] {
  const prevMap = new Map<string, string>();
  (previous ?? []).forEach((s) => prevMap.set(s.id, s.desc ? 'desc' : 'asc'));

  const currMap = new Map<string, string>();
  current.forEach((s) => currMap.set(s.id, s.desc ? 'desc' : 'asc'));

  const changes: string[] = [];

  // Added or direction-changed
  currMap.forEach((dir, id) => {
    const prevDir = prevMap.get(id);
    if (prevDir !== dir) {
      changes.push(`${id}:${dir}`);
    }
  });

  // Removed sort columns
  prevMap.forEach((_, id) => {
    if (!currMap.has(id)) changes.push(`${id}:removed`);
  });

  return changes;
}

export const getFilterBucketCount = (numFilters: number): '0' | '1-2' | '3-5' | '6+' => {
  return getBucketCount(numFilters, [0, 2, 5], ['0', '1-2', '3-5', '6+']) as
    | '0'
    | '1-2'
    | '3-5'
    | '6+';
};

export const getColumnBucketCount = (
  numColumns: number
): '0' | '1-2' | '3-5' | '6-8' | '9-12' | '12+' => {
  return getBucketCount(numColumns, [0, 2, 5, 8, 12], ['0', '1-2', '3-5', '6-8', '9-12', '12+']) as
    | '0'
    | '1-2'
    | '3-5'
    | '6-8'
    | '9-12'
    | '12+';
};

/**
 * @param filters the processed filters object
 * @returns presence flags like { f_modality: true, f_date: true, ... } + filter_count_bucket
 */
export function buildFilterDetails(
  filters: Nullable<Record<string, unknown>>
): FilterPresenceDetails {
  const details: Record<string, unknown> = {};
  const keys: string[] = [];

  if (!filters) {
    details.filterCountBucket = '0';
    return details as FilterPresenceDetails;
  }

  for (const rawKey of Object.keys(filters)) {
    if (rawKey === 'views') {
      continue;
    }

    const val = filters[rawKey] as Nullable<ProcessedFilterValueType>;
    if (!isFilterPresent(val)) continue;

    const k = String(rawKey);

    keys.push(k);
  }

  const uniqueSorted = Array.from(new Set(keys)).sort();
  if (uniqueSorted.length > 0) {
    details.filterKeys = uniqueSorted;
  }

  details.filterCountBucket = getFilterBucketCount(uniqueSorted.length);
  return details as FilterPresenceDetails;
}

/**
 *
 * @param activeCustomWorklistIDs - array of active custom worklist IDs
 * @param allWorklists  - map of all worklists by ID
 * @param isPrivateWorklist  - function to determine if a worklist ID is private
 * @returns  object containing worklistTypes and worklistMode, where worklistTypes is an array
 * of unique worklist types (public, private, canned, legacy) and worklistMode is either 'single' or 'combined'
 */
export const getWorklistTypeDetails = (
  activeCustomWorklistIDs: Array<string>,
  allWorklists: Map<string, CustomWorklist>,
  isPrivateWorklist: (id: string) => boolean
): { worklistTypes: Array<WorklistTypeDetailType>; worklistMode: WorklistModeType } => {
  if (activeCustomWorklistIDs.length === 0) {
    return { worklistTypes: [WorklistTypeDetail.Legacy], worklistMode: WorklistMode.Single };
  }

  const worklistTypes = new Set<WorklistTypeDetailType>();

  for (const worklistID of activeCustomWorklistIDs) {
    const worklist = allWorklists.get(worklistID);

    if (worklist?.internalSettings?.isCanned) {
      worklistTypes.add(WorklistTypeDetail.Canned);
      continue;
    }

    if (isPrivateWorklist(worklistID)) {
      worklistTypes.add(WorklistTypeDetail.Private);
    } else {
      worklistTypes.add(WorklistTypeDetail.Public);
    }
  }

  return {
    worklistTypes: Array.from(worklistTypes).sort(),
    worklistMode: activeCustomWorklistIDs.length > 1 ? WorklistMode.Combined : WorklistMode.Single,
  };
};
